import {IonIcon, IonItem, IonLabel, useIonRouter} from "@ionic/react";
import {linkEmplTicketDetail} from "app/employee/core/employeeLinks";
import type {TicketListFragment, UserFragment} from "app/gql/graphqlSchema";
import {useTranslation} from "react-i18next";
import {styled} from "styled-components";
import {arrowRedoOutline, attachOutline} from "ionicons/icons";
import {isNotNil} from "app/utils/stdlib";
import {RelativeDateDisplay} from "app/employee/RelativeDateDisplay";
import {TicketNumber} from "app/employee/ticket/TicketNumber";
import {ticketItemColor, ticketStatusIcon} from "app/employee/ticket/ticketStatusIcon";
import {StatusBadge} from "app/employee/ticket/StatusBadge";
import type {IonItemProps} from "app/utils/types";

interface Props {
  ticket: TicketListFragment;
  lines?: IonItemProps["lines"];
}

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.25rem 0;
  min-width: 0;
`;

const TopRow = styled.div`
  font-size: 0.8rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
`;

const SenderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
`;

const SenderName = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MessageCount = styled.span`
  font-size: 0.8rem;
`;

const DateText = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.8rem;
`;

const SubjectRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
`;

const Subject = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
`;

const ContentPreview = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ion-color-medium);
  font-size: 0.85rem;
`;

const AttachmentIcon = styled(IonIcon)`
  color: var(--ion-color-medium);
  flex-shrink: 0;
`;

const getUserName = (user: UserFragment) => user.name;

const hasAttachment = (ticket: TicketListFragment) =>
  ticket.attachmentCollection.attachments.length > 0 || isNotNil(ticket.controlCardReference);

export const TicketItem = ({ticket, lines}: Props) => {
  const {t} = useTranslation();
  const ionRouter = useIonRouter();

  const handleClick = () => {
    ionRouter.push(linkEmplTicketDetail(ticket.id.ticketId), "forward");
  };

  return (
    <IonItem button color={ticketItemColor(ticket.status)} detail lines={lines} onClick={handleClick}>
      <IonLabel>
        <ItemContent>
          <TopRow>
            <TicketNumber ticketId={ticket.id} />
            <StatusBadge>
              <IonIcon icon={ticketStatusIcon[ticket.status]} /> {t(`ticket.status.${ticket.status}`)}
            </StatusBadge>
          </TopRow>
          <SenderRow>
            <IonIcon icon={arrowRedoOutline} />
            <SenderName>{getUserName(ticket.sender)}</SenderName>
            {ticket.userMessageCount > 0 && <MessageCount> ({ticket.userMessageCount})</MessageCount>}
            <DateText>
              <RelativeDateDisplay dt={ticket.createdAt} />
            </DateText>
          </SenderRow>
          <SubjectRow>
            <Subject>{ticket.subject}</Subject>
            {hasAttachment(ticket) && <AttachmentIcon icon={attachOutline} />}
          </SubjectRow>
          {ticket.content && <ContentPreview>{ticket.content}</ContentPreview>}
        </ItemContent>
      </IonLabel>
    </IonItem>
  );
};
